import type { ProfileConfig } from "@worker/config-schema";
import { prefersReducedMotion } from "../lib/dom";

/**
 * Apply a username effect to the rendered .username element. Most effects are
 * driven by CSS classes (see profile.css), but a few (sparkles, fuzzy) need
 * JS to spawn/mutate children over time.
 *
 * Returns a teardown function so preview swaps can cleanly switch effects.
 */
export function applyUsernameEffect(
  root: HTMLElement,
  profile: ProfileConfig["profile"],
  motion: ProfileConfig["motion"],
): () => void {
  const effect = profile.usernameEffect;
  if (effect === "none") return () => {};

  const suppress =
    motion.respectReducedMotion &&
    motion.reducedMotionSuppresses.includes("usernameEffect") &&
    prefersReducedMotion();
  if (suppress) return () => {};

  if (effect.startsWith("sparkles-")) {
    return mountSparkles(root, effect);
  }
  // Pure CSS effects (no JS runtime needed): rainbow, glitch, wave, neon,
  // chromatic, fuzzy (CSS jitter keyframe), fire, chrome, emboss.
  return () => {};
}

// ─── Sparkles ─────────────────────────────────────────────────────────────

const SPARKLE_PALETTES: Record<string, string[]> = {
  "sparkles-black": ["#111", "#222", "#333"],
  "sparkles-blue": ["#60a5fa", "#3b82f6", "#93c5fd"],
  "sparkles-pink": ["#f472b6", "#ec4899", "#f9a8d4"],
  "sparkles-rainbow": ["#f87171", "#fbbf24", "#34d399", "#60a5fa", "#a78bfa", "#f472b6"],
};

function mountSparkles(root: HTMLElement, effect: string): () => void {
  const palette = SPARKLE_PALETTES[effect] ?? SPARKLE_PALETTES["sparkles-black"]!;
  const layer = document.createElement("span");
  layer.className = "username__sparkles";
  layer.setAttribute("aria-hidden", "true");
  root.appendChild(layer);

  let running = true;

  const spawn = () => {
    if (!running) return;
    const rect = root.getBoundingClientRect();
    const rootRect = layer.getBoundingClientRect();
    const p = document.createElement("span");
    p.className = "username__sparkle";
    const color = palette[Math.floor(Math.random() * palette.length)]!;
    p.style.setProperty("--tint", color);
    p.style.left = `${Math.random() * rect.width - (rootRect.left - rect.left)}px`;
    p.style.top = `${Math.random() * rect.height}px`;
    p.style.setProperty("--life", `${600 + Math.random() * 500}ms`);
    p.style.setProperty("--size", `${2 + Math.random() * 3}px`);
    layer.append(p);
    window.setTimeout(() => p.remove(), 1200);
    window.setTimeout(spawn, 70 + Math.random() * 100);
  };
  spawn();

  return () => {
    running = false;
    layer.remove();
  };
}

// Fuzzy is now pure CSS (keyframe jitter on .username--fuzzy). See
// profile.css for the implementation. No JS handler needed.
